<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h6 class="mb-0"><i class="fas fa-industry me-1" style="color: #2e389b;"></i> Producción — Órdenes de producción</h6>
              <div class="d-flex gap-2">
                <button
                  type="button"
                  class="btn btn-sm"
                  style="background-color: #6f7a93; border-color: #6f7a93; color: #fff;"
                  :disabled="downloadingPending"
                  @click="downloadPendingItemsGrouped"
                >
                  <i class="fas" :class="downloadingPending ? 'fa-spinner fa-spin' : 'fa-file-pdf'"></i>
                  PDF prod. agrupado
                </button>
              </div>
            </div>
            <p class="text-xs text-secondary mb-3">
              Producciones creadas desde las ventas (remisiones). Cada orden relaciona las ventas enviadas
              y los productos pendientes que entraron a fabricación «En proceso».
            </p>
          </div>

          <div class="card-body px-0 pt-0 pb-2">
            <div v-if="loading" class="text-center py-5">
              <i class="fas fa-spinner fa-spin fa-2x" style="color: #2e389b;"></i>
              <p class="mt-2 text-sm text-secondary">Cargando producciones...</p>
            </div>

            <div v-else class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style="width: 40px">
                      <input
                        type="checkbox"
                        style="width: 16px; height: 16px; cursor: pointer; accent-color: #2dce89;"
                        :checked="allSelected"
                        title="Seleccionar todas las producciones de la página"
                        @change="toggleSelectAll"
                      />
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">N°</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Ventas</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Pares</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Fecha</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Creada por</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Estado</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Opciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="order in data" :key="order.id">
                    <td class="text-center align-middle">
                      <input
                        type="checkbox"
                        style="width: 16px; height: 16px; cursor: pointer; accent-color: #2dce89;"
                        :value="order.id"
                        v-model="selectedIds"
                      />
                    </td>
                    <td class="align-middle ps-2">
                      <span class="text-sm font-weight-bold">#{{ order.id }}</span>
                    </td>
                    <td class="align-middle text-center">
                      <div class="d-flex flex-wrap justify-content-center gap-1">
                        <router-link
                          v-for="remission in order.remissions"
                          :key="remission.id"
                          :to="{ name: 'Production Order', query: { id: remission.id } }"
                          class="badge badge-sm text-decoration-none"
                          style="background-color: #2e389b;"
                          :title="`Ver producción de la venta #${remission.id} — ${remission.name || ''}`"
                        >
                          #{{ remission.id }}
                        </router-link>
                        <span v-if="!(order.remissions || []).length" class="text-xs text-secondary">—</span>
                      </div>
                    </td>
                    <td class="text-center align-middle">
                      <span class="badge badge-sm bg-secondary">{{ order.pairs || 0 }} pares</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ formatDate(order.created_at) }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs">{{ order.creator?.name || '—' }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span :class="['badge badge-sm', orderStatusBadgeClass(order.status)]">
                        {{ orderStatusLabel(order.status) }}
                      </span>
                    </td>
                    <td class="text-center align-middle">
                      <router-link
                        :to="{ name: 'Production Order Details', query: { id: order.id } }"
                        class="btn btn-outline-success btn-sm mb-0"
                        title="Ver detalles de la producción"
                      >
                        <i class="fas fa-eye me-1"></i> Ver detalles
                      </router-link>
                    </td>
                  </tr>

                  <tr v-if="data.length === 0">
                    <td colspan="8" class="text-center text-xxs">No hay producciones creadas. Envíe ventas a producción desde el módulo Ventas / Ingresos.</td>
                  </tr>
                </tbody>
              </table>

              <!-- Paginación -->
              <div class="pagination p-3">
                <a
                  v-for="link in links"
                  :key="link.label"
                  class="page-item cursor-pointer"
                  @click="fetchData(link.url)"
                  :disabled="!link.url || link.active"
                  :class="{ active: link.label === currentPage.toString() }"
                >
                  {{ link.label }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import productionOrdersService from '@/services/productionOrders.service';

export default {
  name: 'ProductionInProgress',
  data() {
    return {
      data: [],
      links: [],
      currentPage: 1,
      lastPage: 1,
      page: 1,
      loading: true,
      selectedIds: [],
      downloadingPending: false,
    };
  },
  computed: {
    allSelected() {
      return this.data.length > 0 && this.data.every((d) => this.selectedIds.includes(d.id));
    },
  },
  methods: {
    async fetchData(pageUrl = null) {
      this.loading = true;
      if (pageUrl) {
        const parsed = parseInt(new window.URL(pageUrl).searchParams.get('page'));
        this.page = Number.isNaN(parsed) ? 1 : parsed;
      }

      try {
        const res = await productionOrdersService.getAll(this.page);
        this.data = res.data.data || [];
        this.links = res.data.links || [];
        this.currentPage = res.data.current_page || 1;
        this.lastPage = res.data.last_page || 1;
        this.selectedIds = [];
      } catch (error) {
        console.error('Error al cargar producciones:', error);
        await Swal.fire('Error', 'No se pudo cargar las producciones', 'error');
      } finally {
        this.loading = false;
      }
    },
    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedIds = this.data.map((d) => d.id);
      } else {
        this.selectedIds = [];
      }
    },
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleDateString('es-ES');
    },
    orderStatusLabel(status) {
      if (status === 'fabricado') return 'Fabricado';
      return 'En proceso';
    },
    orderStatusBadgeClass(status) {
      if (status === 'fabricado') return 'bg-primary';
      return 'bg-warning text-dark';
    },
    async downloadPendingItemsGrouped() {
      if (this.selectedIds.length === 0) {
        await Swal.fire('Atención', 'Seleccione al menos una producción', 'warning');
        return;
      }
      this.downloadingPending = true;
      try {
        const response = await productionOrdersService.getPendingItemsGrouped(this.selectedIds);
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `produccion_agrupado_${new Date().toISOString().slice(0, 10)}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al generar PDF de producciones:', error);
        await Swal.fire('Error', 'No se pudo generar el PDF de producciones', 'error');
      } finally {
        this.downloadingPending = false;
      }
    },
  },
  async mounted() {
    await this.fetchData();
  },
};
</script>

<style scoped>
.pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.page-item {
  margin: 0 5px;
  padding: 4px 10px;
  cursor: pointer;
  border-radius: 6px;
}

.page-item.active {
  font-weight: bold;
  background: #2dce89;
  color: #fff;
}

.page-item[disabled] {
  opacity: 0.5;
  pointer-events: none;
}
</style>
